import { repairLatexEscapes } from './repairLatexEscapes';

/**
 * Broad latex / math signals for home-card sanitization.
 * Prefer generic `\\command` over a brittle allowlist — `\dfrac`, `\int_0`,
 * `\textbf`, `\in`, `\quad`, unclosed `$…` were previously missed and leaked.
 */
const LATEX_SIGNAL_PATTERN = /(\$|\\\(|\\\[|\\[a-zA-Z]+|[_^](?:\{[^}]*\}|[a-zA-Z0-9]))/;

const JSON_FIELD_NOISE_PATTERN = /\b(?:subQuestions?|subAnswers?|mainQuestion|mainAnswer|questions?_answers?|questionDisplay|answerDisplay)\b|"subQuestions?"\s*:|"subAnswers?"\s*:|"main"\s*:/i;

const PARTICLE_TYPE_TAG_PATTERN = /^(\[(?:听写稿|阅读稿|语法题|口译稿|文言文阅读稿|阅读理解稿|写作积累|语文试题)\])\s*/;

const PREVIEW_BLANK = '___';

export function containsLatexOrMathMarkup(text: string): boolean {
  return LATEX_SIGNAL_PATTERN.test(String(text || '').trim());
}

export function containsJsonFieldNoise(text: string): boolean {
  const normalized = String(text || '').trim();
  if (!normalized) {
    return false;
  }

  if (!JSON_FIELD_NOISE_PATTERN.test(normalized)) {
    return false;
  }

  return /[{[\]}":]/.test(normalized)
    || normalized.startsWith('{')
    || normalized.startsWith('[');
}

function isBareJsonFieldLabel(text: string): boolean {
  const normalized = String(text || '').trim().toLowerCase();
  return normalized === 'subquestions'
    || normalized === 'subanswers'
    || normalized === 'subquestion'
    || normalized === 'subanswer'
    || normalized === 'mainquestion'
    || normalized === 'mainanswer';
}

function looksLikePureJsonContainer(text: string): boolean {
  const normalized = String(text || '').trim();
  if (!normalized) {
    return false;
  }

  if ((normalized.startsWith('{') && normalized.endsWith('}'))
    || (normalized.startsWith('[') && normalized.endsWith(']'))) {
    return containsJsonFieldNoise(normalized) || /"\w+"\s*:/.test(normalized);
  }

  return false;
}

export function extractParticleTypeTag(text: string): { tag: string; rest: string } {
  const normalized = String(text || '').trim();
  const match = normalized.match(PARTICLE_TYPE_TAG_PATTERN);
  if (!match?.[1]) {
    return { tag: '', rest: normalized };
  }

  return {
    tag: match[1],
    rest: normalized.slice(match[0].length).trim(),
  };
}

function unescapeJsonString(value: string): string {
  // 先按 Mobile2 修好 \t 族命令，再处理 JSON 空白转义（禁止裸 replace /\\t/）
  const LATEX_T_COMMAND_PATTERN = /\\(times|text|theta|triangle|tan|to)\b/g;
  const tokens: string[] = [];
  const protectedText = String(value || '').replace(LATEX_T_COMMAND_PATTERN, (match) => {
    const index = tokens.length;
    tokens.push(match);
    return `\uE000L${index}\uE001`;
  });
  const unescaped = protectedText
    .replace(/\\n/g, ' ')
    .replace(/\\r/g, ' ')
    .replace(/\\t/g, ' ')
    .replace(/\t/g, ' ')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\')
    .replace(/\uE000L(\d+)\uE001/g, (_, index: string) => tokens[Number(index)] || '');
  return repairLatexEscapes(unescaped).trim();
}

function extractReadableFromJsonish(text: string): string {
  const normalized = String(text || '').trim();
  if (!normalized) {
    return '';
  }

  const quotedPatterns = [
    /"subQuestion"\s*:\s*"((?:\\.|[^"\\])*)"/gi,
    /"subAnswer"\s*:\s*"((?:\\.|[^"\\])*)"/gi,
    /"main"\s*:\s*"((?:\\.|[^"\\])*)"/gi,
    /"question"\s*:\s*"((?:\\.|[^"\\])*)"/gi,
    /"title"\s*:\s*"((?:\\.|[^"\\])*)"/gi,
  ];

  for (const pattern of quotedPatterns) {
    const matches = [...normalized.matchAll(pattern)]
      .map((match) => unescapeJsonString(match[1] || ''))
      .filter(Boolean);
    if (matches.length) {
      return matches.join(' ');
    }
  }

  if ((normalized.startsWith('{') && normalized.endsWith('}')) || (normalized.startsWith('[') && normalized.endsWith(']'))) {
    try {
      const parsed = JSON.parse(normalized) as unknown;
      return readPreviewSourceText(parsed);
    } catch {
      // fall through
    }
  }

  return normalized
    .replace(JSON_FIELD_NOISE_PATTERN, ' ')
    .replace(/"\w+"\s*:/g, ' ')
    .replace(/[{}\[\]]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function stripJsonNoiseForPreview(text: string): string {
  const normalized = String(text || '').trim();
  if (!normalized) {
    return '';
  }

  if (isBareJsonFieldLabel(normalized)) {
    return '';
  }

  if (containsJsonFieldNoise(normalized) || looksLikePureJsonContainer(normalized)) {
    return extractReadableFromJsonish(normalized);
  }

  return normalized;
}

function findClosingSingleDollar(text: string, start: number): number {
  let depth = 0;

  for (let index = start; index < text.length; index += 1) {
    const char = text[index];
    if (char === '\\') {
      index += 1;
      continue;
    }
    if (char === '{') {
      depth += 1;
      continue;
    }
    if (char === '}') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (char === '$' && depth === 0) {
      return index;
    }
  }

  return -1;
}

function findClosingDoubleDollar(text: string, start: number): number {
  let depth = 0;

  for (let index = start; index < text.length - 1; index += 1) {
    const char = text[index];
    if (char === '\\') {
      index += 1;
      continue;
    }
    if (char === '{') {
      depth += 1;
      continue;
    }
    if (char === '}') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (char === '$' && text[index + 1] === '$' && depth === 0) {
      return index;
    }
  }

  return -1;
}

function replaceDollarMathSegments(text: string, blank: string): string {
  let result = '';
  let index = 0;

  while (index < text.length) {
    if (text[index] === '$' && text[index + 1] === '$') {
      const end = findClosingDoubleDollar(text, index + 2);
      if (end >= 0) {
        result += ` ${blank} `;
        index = end + 2;
        continue;
      }
    }

    if (text[index] === '$') {
      const end = findClosingSingleDollar(text, index + 1);
      if (end >= 0) {
        result += ` ${blank} `;
        index = end + 1;
        continue;
      }
    }

    result += text[index];
    index += 1;
  }

  return result;
}

function replaceBalancedLatexCommands(text: string, blank: string): string {
  let result = text;

  for (let pass = 0; pass < 24; pass += 1) {
    const next = result.replace(/\\[a-zA-Z]+\*?(?:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\})+/g, ` ${blank} `);
    if (next === result) {
      break;
    }
    result = next;
  }

  return result;
}

export function stripLatexForPreview(text: string, blank = PREVIEW_BLANK): string {
  let result = String(text || '').trim();
  if (!result) {
    return '';
  }

  result = replaceDollarMathSegments(result, blank)
    .replace(/\\\([\s\S]+?\\\)/g, ` ${blank} `)
    .replace(/\\\[([\s\S]+?)\\\]/g, ` ${blank} `);

  result = replaceBalancedLatexCommands(result, blank);

  result = result
    .replace(/\\[a-zA-Z]+/g, ' ')
    .replace(/[_^]\{[^}]*\}/g, ' ')
    .replace(/[_^][a-zA-Z0-9]/g, ' ')
    .replace(/\*\*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const blankPattern = new RegExp(`(?:${blank}\\s*)+`, 'g');
  return result.replace(blankPattern, `${blank} `).trim();
}

const LATEX_TEXT_COMMAND_PATTERN = /\\(?:text|textrm|textit|textbf|mathrm|mathbf|mathsf|mathtt|mbox|operatorname)\*?\{([^{}]*)\}/g;

/** Readable text kept inside leaked latex on language-subject home cards. */
const LANGUAGE_CARD_TEXT_CHAR_PATTERN = /[\u3400-\u9fffA-Za-z0-9\u3000-\u303f\uff00-\uffef，。！？、；：""''“”‘’（）()\-\u2013\u2014…·\s]/;

/**
 * Lift \\text{...} content for language cards.
 * Skip dictation phantom marker \\text{Xy}.
 */
function liftMeaningfulLatexTextCommands(text: string): string {
  let result = String(text || '');
  for (let pass = 0; pass < 16; pass += 1) {
    const next = result.replace(LATEX_TEXT_COMMAND_PATTERN, (_match, inner: string) => {
      const content = String(inner || '').trim();
      if (!content || /^Xy$/i.test(content)) {
        return ' ';
      }
      return ` ${content} `;
    });
    if (next === result) {
      break;
    }
    result = next;
  }
  return result;
}

function collapsePreviewBlanks(text: string, blank: string): string {
  const blankPattern = new RegExp(`(?:${blank.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*)+`, 'g');
  return text.replace(blankPattern, `${blank} `).replace(/\s+/g, ' ').trim();
}

/**
 * Soften latex body for 语文/英语 cards: keep readable text, replace the rest with ___.
 * Dimension tokens (1.35em / 0pt) and phantom markers are treated as non-text.
 */
function softenLatexBodyKeepText(inner: string, blank: string): string {
  let body = liftMeaningfulLatexTextCommands(inner)
    .replace(/\\[a-zA-Z]+\*?/g, ' ')
    .replace(/[{}\[\]$_^&#\\]/g, ' ')
    .replace(/\bXy\b/gi, ' ')
    .replace(/\b\d*\.?\d+(?:pt|em|ex|mu)\b/gi, ' ');

  let result = '';
  let pendingBlank = false;
  for (const char of body) {
    if (LANGUAGE_CARD_TEXT_CHAR_PATTERN.test(char)) {
      if (pendingBlank && result && !/\s$/.test(result)) {
        result += ` ${blank} `;
      }
      pendingBlank = false;
      result += char;
      continue;
    }
    pendingBlank = true;
  }
  if (pendingBlank) {
    result += result ? ` ${blank}` : blank;
  }

  result = collapsePreviewBlanks(result, blank);
  // Pure digits / punctuation leftovers (e.g. $^2$, \frac{1}{2}) → blank for card cohesion
  if (!result || !/[\u3400-\u9fffA-Za-z]/.test(result)) {
    return '';
  }
  return result;
}

function softenMathSegment(inner: string, blank: string): string {
  return softenLatexBodyKeepText(inner, blank) || blank;
}

function replaceDollarMathSegmentsKeepText(text: string, blank: string): string {
  let result = '';
  let index = 0;

  while (index < text.length) {
    if (text[index] === '$' && text[index + 1] === '$') {
      const end = findClosingDoubleDollar(text, index + 2);
      if (end >= 0) {
        result += ` ${softenMathSegment(text.slice(index + 2, end), blank)} `;
        index = end + 2;
        continue;
      }
    }

    if (text[index] === '$') {
      const end = findClosingSingleDollar(text, index + 1);
      if (end >= 0) {
        result += ` ${softenMathSegment(text.slice(index + 1, end), blank)} `;
        index = end + 1;
        continue;
      }
    }

    result += text[index];
    index += 1;
  }

  return result;
}

/**
 * 首页素材卡片：若标题/预览泄露 latex，保留公式内可读文本，
 * 将其余非文本符号统一替换为 ___，避免卡片上出现原始公式噪声。
 */
export function softenLatexLeakForLanguageCard(text: string, blank = PREVIEW_BLANK): string {
  const source = String(text || '').trim();
  if (!source) {
    return '';
  }
  if (!containsLatexOrMathMarkup(source)) {
    return source;
  }

  let result = replaceDollarMathSegmentsKeepText(source, blank)
    .replace(/\\\(([\s\S]+?)\\\)/g, (_match, inner: string) => ` ${softenMathSegment(inner, blank)} `)
    .replace(/\\\[([\s\S]+?)\\\]/g, (_match, inner: string) => ` ${softenMathSegment(inner, blank)} `);

  result = liftMeaningfulLatexTextCommands(result);
  result = replaceBalancedLatexCommands(result, blank);
  result = result
    .replace(/\\[a-zA-Z]+\*?/g, ` ${blank} `)
    .replace(/[_^]\{[^}]*\}/g, ` ${blank} `)
    .replace(/[_^][a-zA-Z0-9]/g, ` ${blank} `)
    .replace(/\b\d*\.?\d+(?:pt|em|ex|mu)\b/gi, ` ${blank} `)
    .replace(/\$+/g, ' ')
    .replace(/[{}]/g, ' ')
    .replace(/\\/g, ' ')
    .replace(/&=/g, '=')
    .replace(/&/g, ' ')
    .replace(/\*\*/g, ' ');

  return collapsePreviewBlanks(result, blank);
}

function readPreviewSourceText(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const preferredKeys = [
      'main',
      'title',
      'displayTitle',
      'name',
      'question',
      'stem',
      'summary',
      'description',
      'brief',
      'content',
      'text',
      'value',
      'label',
      'subQuestion',
      'subAnswer',
    ];

    for (const key of preferredKeys) {
      const candidate = record[key];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
  }

  return '';
}

function readPreviewInput(value: unknown): string {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  const extracted = readPreviewSourceText(value);
  if (extracted) {
    return extracted;
  }

  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }

  return String(value || '').trim();
}

function truncatePreviewText(text: string, maxLength: number): string {
  const normalized = String(text || '').trim();
  if (!normalized || normalized.length <= maxLength) {
    return normalized;
  }

  const slice = normalized.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(' ');
  const trimmed = (lastSpace > Math.floor(maxLength * 0.55) ? slice.slice(0, lastSpace) : slice).trim();
  return trimmed ? `${trimmed}...` : `${normalized.slice(0, maxLength).trim()}...`;
}

function composeTaggedPreview(tag: string, body: string, maxLength: number): string {
  if (!tag) {
    return truncatePreviewText(body, maxLength);
  }

  if (!body) {
    return truncatePreviewText(tag, maxLength);
  }

  const bodyBudget = Math.max(maxLength - tag.length - 1, 8);
  const trimmedBody = truncatePreviewText(body, bodyBudget);
  return truncatePreviewText(`${tag} ${trimmedBody}`, maxLength);
}

function sanitizePreviewBody(source: string, maxLength: number): string {
  const withoutJsonNoise = stripJsonNoiseForPreview(source);
  if (!withoutJsonNoise) {
    return containsLatexOrMathMarkup(source) ? PREVIEW_BLANK : '';
  }

  const withoutLatex = stripLatexForPreview(withoutJsonNoise);
  if (!withoutLatex) {
    return containsLatexOrMathMarkup(withoutJsonNoise) ? PREVIEW_BLANK : '';
  }

  if (looksLikePureJsonContainer(withoutLatex) || isBareJsonFieldLabel(withoutLatex)) {
    return '';
  }

  return truncatePreviewText(withoutLatex, maxLength);
}

export function formatParticlePreviewPlainText(value: unknown, maxLength = 120): string {
  const source = readPreviewInput(value);
  if (!source) {
    return '';
  }

  const { tag, rest } = extractParticleTypeTag(source);
  const body = sanitizePreviewBody(rest || source, maxLength);
  if (tag) {
    return composeTaggedPreview(tag, body, maxLength);
  }

  return body;
}

export function formatParticlePreviewTitle(value: unknown, maxLength = 48): string {
  const source = readPreviewInput(value);
  if (!source) {
    return '';
  }

  const { tag, rest } = extractParticleTypeTag(source);
  const bodySource = rest || source;
  let body = sanitizePreviewBody(bodySource, maxLength);

  if (!body && containsLatexOrMathMarkup(bodySource)) {
    body = PREVIEW_BLANK;
  }

  if (tag || body) {
    return composeTaggedPreview(tag, body, maxLength);
  }

  return '';
}

export function formatParticlePreviewMetaText(value: unknown, maxLength = 56): string {
  const source = readPreviewInput(value);
  if (!source) {
    return '';
  }

  if (isBareJsonFieldLabel(source)) {
    return '';
  }

  const { tag, rest } = extractParticleTypeTag(source);
  const bodySource = rest || source;
  let body = sanitizePreviewBody(bodySource, maxLength);

  if (!body && (containsLatexOrMathMarkup(bodySource) || containsJsonFieldNoise(bodySource))) {
    body = containsLatexOrMathMarkup(bodySource) ? PREVIEW_BLANK : '';
  }

  if (tag || body) {
    return composeTaggedPreview(tag, body, maxLength);
  }

  return '';
}
